import { UserRole } from '../common/api.types.js'

// Shared role-hierarchy helpers. These live in @atlas/types — the browser-safe
// contract package both the backend and the frontend already depend on — so there
// is a single source of truth for the role ordering. (The frontend cannot import
// @atlas/core-utils/auth: that barrel pulls in Drizzle and aws-jwt-verify.)
// Role enforcement remains authoritative on the backend; the frontend uses these
// only for UX gating.

const ROLE_ORDINAL: Record<UserRole, number> = {
	[UserRole.VIEWER]: 0,
	[UserRole.USER]: 1,
	[UserRole.ADMIN]: 2,
	[UserRole.SUPER_ADMIN]: 3,
}

/** True when `userRole` ranks at or above `minRole` in the fixed hierarchy. */
export function roleAtLeast(userRole: UserRole | string, minRole: UserRole): boolean {
	return ROLE_ORDINAL[userRole as UserRole] >= ROLE_ORDINAL[minRole]
}

const rolesAtLeast = (minRole: UserRole): UserRole[] =>
	(Object.keys(ROLE_ORDINAL) as UserRole[]).filter((role) => ROLE_ORDINAL[role] >= ROLE_ORDINAL[minRole])

// The four gates the product actually has. Every route's `roles` is one of
// these, so no controller spells out a role array of its own — the hand-written
// arrays are what let USER fall out of the read sets by typo. Derived from the
// ordinal map rather than listed, so a new role lands in the right sets.

/** Any non-admin read. The whole product is readable by every role. */
export const READ_ROLES: UserRole[] = rolesAtLeast(UserRole.VIEWER)
/** Ordinary editorial work: create, edit, transition, classify, comment, upload. */
export const WORK_ROLES: UserRole[] = rolesAtLeast(UserRole.USER)
/** System configuration, and deleting work that is not yours. */
export const ADMIN_ROLES: UserRole[] = rolesAtLeast(UserRole.ADMIN)
/** Tenant ownership: users, roles, webhooks, external integrations. */
export const SUPER_ADMIN_ROLES: UserRole[] = rolesAtLeast(UserRole.SUPER_ADMIN)

/** True when the role is ADMIN or SUPER_ADMIN. Fails closed for absent roles. */
export function isAdmin(role: UserRole | string | undefined | null): boolean {
	return role !== undefined && role !== null && roleAtLeast(role as UserRole, UserRole.ADMIN)
}
